<template>
  <div class="create-settlement-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>结算发起</span>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-area">
      <el-input
        v-model="searchParams.orderNumber"
        placeholder="订单编号"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
        @keyup.enter.native="handleSearch"
      ></el-input>

      <el-input
        v-model="searchParams.productName"
        placeholder="产品名称"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      
      <el-input
        v-model="searchParams.demandParty"
        placeholder="需求方"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      
      <el-input
        v-model="searchParams.secondaryUnit"
        placeholder="二级单位"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 产品列表 -->
    <div class="products-section">
      <div class="section-header">
        <h3>发货记录产品明细列表</h3>
        <div class="header-actions">
          <el-button 
            v-if="activeSettlementTab === 'UNSETTLED'"
            @click="toggleSelectAll"
            :type="allSelected ? 'success' : 'primary'"
            size="small"
          >
            {{ allSelected ? '取消全选' : '全选' }}
          </el-button>
        </div>
      </div>

      <!-- 结算状态Tab -->
      <div class="settlement-status-tabs">
        <el-tabs v-model="activeSettlementTab" @tab-click="handleSettlementTabChange">
          <el-tab-pane name="UNSETTLED">
            <template slot="label">
              <span class="tab-label">
                <i class="el-icon-time"></i>
                未结算
              </span>
            </template>
          </el-tab-pane>
          <el-tab-pane name="SETTLED">
            <template slot="label">
              <span class="tab-label">
                <i class="el-icon-circle-check"></i>
                已结算
              </span>
            </template>
          </el-tab-pane>
          <el-tab-pane name="ALL">
            <template slot="label">
              <span class="tab-label">
                <i class="el-icon-menu"></i>
                全部
              </span>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 定价方式Tab -->
      <div class="pricing-tabs">
        <el-tabs v-model="activePricingTab" @tab-click="handlePricingTabChange">
          <el-tab-pane label="固定价产品" name="FIXED">
            <template slot="label">
              <span class="tab-label">
                <i class="el-icon-price-tag"></i>
                固定价产品
              </span>
            </template>
          </el-tab-pane>
          <el-tab-pane label="变动价产品" name="FLOATING">
            <template slot="label">
              <span class="tab-label">
                <i class="el-icon-s-finance"></i>
                变动价产品
              </span>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>

      <el-table
        :data="filteredProducts"
        border
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
        :row-class-name="getProductRowClassName"
      >
        <el-table-column width="55" align="center">
          <template slot-scope="scope">
            <el-checkbox 
              v-model="scope.row.selected"
              :disabled="!scope.row.canSelect"
              @change="handleProductSelectionChange(scope.row)"
            ></el-checkbox>
          </template>
        </el-table-column>
        
        <el-table-column
          label="订单编号"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.orderNumber || '-' }}
          </template>
        </el-table-column>

        <el-table-column
          label="结算状态"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <el-tag
              :type="isProductSettled(scope.row) ? 'info' : 'success'"
              size="small"
            >
              {{ isProductSettled(scope.row) ? '已结算' : '未结算' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column
          label="需求方"
          width="250"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.demandParty || '-' }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="二级单位"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.secondaryUnit || '-' }}
          </template>
        </el-table-column>
        
        
        <el-table-column
          label="产品名称"
          min-width="150"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.productName || '-' }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="规格型号"
          width="160"
          align="center"
        >
          <template slot-scope="scope">
            <el-tooltip
              v-if="getSpecificationText(scope.row)"
              :content="getSpecificationText(scope.row)"
              placement="top"
              effect="dark"
              popper-class="spec-tooltip"
            >
              <span>{{ truncateText(getSpecificationText(scope.row), 20) }}</span>
            </el-tooltip>
            <span v-else>-</span>
          </template>
        </el-table-column>
        
        <el-table-column
          label="材质要求"
          min-width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.materialRequirement || '-' }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="发货数量"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.shippedQuantity || 0 }} {{ scope.row.unit || '个' }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="剩余数量"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <span :class="{ 'text-danger': scope.row.remainingQuantity <= 0 }">
              {{ scope.row.remainingQuantity || 0 }} {{ scope.row.unit || '个' }}
            </span>
          </template>
        </el-table-column>
        
        <el-table-column
          label="发货日期"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatDate(scope.row.shipmentDate) }}
          </template>
        </el-table-column>
        
        <!-- 固定价产品显示含税单价 -->
        <el-table-column
          v-if="activePricingTab === 'FIXED'"
          label="含税单价"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice || 0) }}
          </template>
        </el-table-column>
        
        <!-- 变动价产品显示变动价和基础价 -->
        <el-table-column
          v-if="activePricingTab === 'FLOATING'"
          label="变动价"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.taxInclusiveFloatingPrice"
              :precision="5"
              :step="0.00001"
              :disabled="!scope.row.selected || !scope.row.canSelect"
              size="mini"
              placeholder="请输入变动价"
              @change="handleFloatingPriceChange(scope.row)"
              style="width: 130px;"
            ></el-input-number>
          </template>
        </el-table-column>
        
        <el-table-column
          v-if="activePricingTab === 'FLOATING'"
          label="含税固定价"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatUnitPrice(scope.row.taxInclusiveBasePrice || 0) }}
          </template>
        </el-table-column>
        
        <el-table-column
          v-if="activePricingTab === 'FLOATING'"
          label="结算价"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <span class="settlement-price">
              {{ formatUnitPrice(calculateFloatingSettlementPrice(scope.row)) }}
            </span>
          </template>
        </el-table-column>
        
        <el-table-column
          label="结算数量"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.settlement_quantity"
              :min="scope.row.shippedQuantity < 0 ? scope.row.shippedQuantity : (scope.row.remainingQuantity < 0 ? scope.row.remainingQuantity : 0)"
              :max="Number(scope.row.remainingQuantity || 0)"
              :precision="4"
              :step="0.0001"
              :disabled="!scope.row.selected || !scope.row.canSelect"
              size="mini"
              @change="handleQuantityChange(scope.row)"
            ></el-input-number>
          </template>
        </el-table-column>
        
        <el-table-column
          label="税率"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            {{ (scope.row.taxRate || 0).toFixed(1) }}%
          </template>
        </el-table-column>
        
        <!-- 实际单价列（只在固定价产品且采管费不为0时显示） -->
        <el-table-column
          v-if="activePricingTab === 'FIXED' && showActualUnitPriceColumn"
          label="实际单价"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatUnitPrice(calculateActualUnitPrice(scope.row)) }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="含税金额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatAmount(calculateProductSettlementAmount(scope.row)) }}
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.current"
          :page-sizes="[5, 10, 20, 50]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        ></el-pagination>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <el-button @click="goBack">取消</el-button>
      <el-button 
        type="primary" 
        @click="goToConfirm"
        :disabled="selectedProductsCount === 0"
      >
        确认结算 ({{ selectedProductsCount }})
      </el-button>
    </div>

    <!-- 确认结算弹窗 -->
    <el-dialog
      v-dialog-drag
      title="确认结算信息"
      :visible.sync="confirmDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="confirm-content" id="printArea">
        <!-- 结算单标题：自动生成，可编辑 -->
        <div class="confirm-settlement-title-section">
          <el-form label-width="100px">
            <el-form-item label="结算单标题">
              <el-input
                v-model="confirmSettlementTitle"
                placeholder="结算单标题"
                maxlength="200"
                show-word-limit
                style="width: 100%;"
              />
              <div class="form-tip">根据订单对应合同与结算日期自动生成，可自行修改</div>
            </el-form-item>
          </el-form>
        </div>
        <!-- 确认产品清单 -->
        <div class="confirm-products">
          <div class="products-header">
            <h3>结算产品清单</h3>
            <el-button v-permission="'settlement:create-settlement:print'" type="primary" size="small" icon="el-icon-printer" @click="printSettlement">打印</el-button>
          </div>
          <el-table
            :data="getSelectedProductsList()"
            border
            style="width: 100%"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <el-table-column
              label="订单编号"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.orderNumber || '-' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="需求方"
              width="150"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.demandParty || '-' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="二级单位"
              width="150"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.secondaryUnit || '-' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="产品名称"
              min-width="150"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.productName || '-' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="规格型号"
              width="160"
              align="center"
            >
              <template slot-scope="scope">
                <el-tooltip
                  v-if="getSpecificationText(scope.row)"
                  :content="getSpecificationText(scope.row)"
                  placement="top"
                  effect="dark"
                  popper-class="spec-tooltip"
                >
                  <span>{{ truncateText(getSpecificationText(scope.row), 20) }}</span>
                </el-tooltip>
                <span v-else>-</span>
              </template>
            </el-table-column>
            
            <el-table-column
              label="材质要求"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.materialRequirement || '-' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="发货数量"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.shippedQuantity || 0 }} {{ scope.row.unit || '个' }}
              </template>
            </el-table-column>
            
            <!-- 固定价产品显示含税单价 -->
            <el-table-column
              v-if="!hasFloatingPriceInSelected()"
              label="含税单价"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice || 0) }}
              </template>
            </el-table-column>
            
            <!-- 变动价产品显示变动价、基础价和结算价 -->
            <el-table-column
              v-if="hasFloatingPriceInSelected()"
              label="变动价"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatUnitPrice(scope.row.taxInclusiveFloatingPrice || 0) }}
              </template>
            </el-table-column>
            
            <el-table-column
              v-if="hasFloatingPriceInSelected()"
              label="含税固定价"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatUnitPrice(scope.row.taxInclusiveBasePrice || 0) }}
              </template>
            </el-table-column>
            
            <el-table-column
              v-if="hasFloatingPriceInSelected()"
              label="结算价"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatUnitPrice(calculateFloatingSettlementPrice(scope.row)) }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="结算数量"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatQuantity(scope.row.settlement_quantity) }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="税率"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ (scope.row.taxRate || 0).toFixed(1) }}%
              </template>
            </el-table-column>
            
            <!-- 实际单价列（只在固定价产品且采管费不为0时显示） -->
            <el-table-column
              v-if="!hasFloatingPriceInSelected() && showActualUnitPriceColumn"
              label="实际单价"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatUnitPrice(calculateActualUnitPrice(scope.row)) }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="含税金额"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatAmount(calculateProductSettlementAmount(scope.row)) }}
              </template>
            </el-table-column>
            
            <!-- 采管费单价列（合计模式时隐藏） -->
            <el-table-column
              v-if="showProcurementManagementFeeColumns"
              label="采管费单价"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                <span v-if="calculateProcurementManagementFeeUnitPrice(scope.row) !== 0">
                  {{ formatProcurementFeeUnitPrice(calculateProcurementManagementFeeUnitPrice(scope.row)) }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            
            <!-- 采管费金额列（合计模式时隐藏） -->
            <el-table-column
              v-if="showProcurementManagementFeeColumns"
              label="采管费金额"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                <span v-if="calculateProcurementManagementFee(scope.row) !== 0">
                  {{ formatAmount(calculateProcurementManagementFee(scope.row)) }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 结算汇总信息 -->
        <div class="confirm-summary">
          <div class="summary-card">
            <div class="summary-title">结算汇总</div>
            <div class="summary-content">
              <div class="summary-item">
                <span>选择产品数量:</span>
                <span>{{ selectedProductsCount }} 项</span>
              </div>
              <div class="summary-item">
                <span>结算总金额:</span>
                <span class="amount highlight">{{ formatAmount(getConfirmDialogSettlementTotal()) }} 元</span>
              </div>
              <!-- 固定价合计模式的采管费总额 -->
              <div v-if="hasFixedPriceInSelected() && fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT' && calculateFixedPriceTotalProcurementManagementFee() !== 0" class="summary-item">
                <span>采管费总额:</span>
                <span class="amount highlight">{{ formatAmount(calculateFixedPriceTotalProcurementManagementFee()) }} 元</span>
              </div>
              <!-- 变动价合计模式的采管费总额 -->
              <div v-if="hasFloatingPriceInSelected() && procurementManagementFeeMode === 'TOTAL_AMOUNT' && calculateFloatingPriceTotalProcurementManagementFee() !== 0" class="summary-item">
                <span>采管费总额:</span>
                <span class="amount highlight">{{ formatAmount(calculateFloatingPriceTotalProcurementManagementFee()) }} 元</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 结算日期和采管费设置 -->
        <div class="settlement-config-section">
          <div class="section-title">结算配置</div>
          <el-row :gutter="20">
            <!-- 结算日期 -->
            <el-col :span="24">
              <el-form :model="{ settlementDate }" :rules="supplierAuditFormRules" ref="settlementDateForm" label-width="120px">
                <el-form-item label="结算日期" prop="settlementDate">
                  <el-date-picker
                    v-model="settlementDate"
                    type="date"
                    placeholder="请选择结算日期"
                    format="yyyy年MM月dd日"
                    value-format="yyyy-MM-dd"
                    style="width: 100%;"
                  ></el-date-picker>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
          
          <!-- 变动价产品采管费设置 -->
          <el-row :gutter="20" v-if="hasFloatingPriceInSelected()">
            <el-col :span="12">
              <el-form :model="{ procurementManagementFeeType }" :rules="supplierAuditFormRules" ref="procurementFeeTypeForm" label-width="150px">
                <el-form-item label="变动价采管费类型" prop="procurementManagementFeeType">
                  <el-radio-group v-model="procurementManagementFeeType">
                    <el-radio label="FIXED_RATE">比率模式</el-radio>
                    <el-radio label="FIXED_UNIT_PRICE">金额模式</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
          
          <!-- 变动价采管费模式选择 -->
          <el-row :gutter="20" v-if="hasFloatingPriceInSelected() && procurementManagementFeeType">
            <el-col :span="12">
              <el-form :model="{ procurementManagementFeeMode }" :rules="supplierAuditFormRules" ref="procurementFeeModeForm" label-width="150px">
                <el-form-item label="变动价采管费模式" prop="procurementManagementFeeMode">
                  <el-radio-group v-model="procurementManagementFeeMode">
                    <el-radio label="UNIT_PRICE">单价模式</el-radio>
                    <el-radio label="TOTAL_AMOUNT">合计模式</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
          
          <el-row :gutter="20" v-if="hasFloatingPriceInSelected() && procurementManagementFeeType && procurementManagementFeeMode">
            <!-- 比率模式 + 单价模式 -->
            <el-col v-if="procurementManagementFeeType === 'FIXED_RATE' && procurementManagementFeeMode === 'UNIT_PRICE'" :span="12">
              <el-form :model="{ procurementManagementFeeRate }" :rules="supplierAuditFormRules" ref="procurementFeeRateForm" label-width="150px">
                <el-form-item label="变动价采管费（%）" prop="procurementManagementFeeRate">
                  <el-input-number
                    v-model="procurementManagementFeeRate"
                    :precision="2"
                    :step="0.01"
                    :min="-100"
                    :max="100"
                    placeholder="请输入采管费百分比"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    单价模式：每个清单项的采管费单价 = (基础价 + 变动价) × 比率%（范围：-100到100，支持两位小数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
            
            <!-- 比率模式 + 合计模式 -->
            <el-col v-if="procurementManagementFeeType === 'FIXED_RATE' && procurementManagementFeeMode === 'TOTAL_AMOUNT'" :span="12">
              <el-form :model="{ procurementManagementFeeRate }" :rules="supplierAuditFormRules" ref="procurementFeeRateForm" label-width="150px">
                <el-form-item label="变动价采管费（%）" prop="procurementManagementFeeRate">
                  <el-input-number
                    v-model="procurementManagementFeeRate"
                    :precision="2"
                    :step="0.01"
                    :min="-100"
                    :max="100"
                    placeholder="请输入采管费百分比"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    合计模式：总采管费 = 结算总金额 × 比率%（范围：-100到100，支持两位小数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
            
            <!-- 金额模式 + 单价模式 -->
            <el-col v-if="procurementManagementFeeType === 'FIXED_UNIT_PRICE' && procurementManagementFeeMode === 'UNIT_PRICE'" :span="12">
              <el-form :model="{ procurementManagementFeeUnitPrice }" :rules="supplierAuditFormRules" ref="procurementFeeUnitPriceForm" label-width="150px">
                <el-form-item label="变动价采管费单价" prop="procurementManagementFeeUnitPrice">
                  <el-input-number
                    v-model="procurementManagementFeeUnitPrice"
                    :precision="5"
                    :step="0.00001"
                    placeholder="请输入采管费单价"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    单价模式：每个清单项的采管费金额 = 采管费单价 × 结算数量（可为负数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
            
            <!-- 金额模式 + 合计模式 -->
            <el-col v-if="procurementManagementFeeType === 'FIXED_UNIT_PRICE' && procurementManagementFeeMode === 'TOTAL_AMOUNT'" :span="12">
              <el-form :model="{ procurementManagementFeeUnitPrice }" :rules="supplierAuditFormRules" ref="procurementFeeUnitPriceForm" label-width="150px">
                <el-form-item label="变动价采管费金额" prop="procurementManagementFeeUnitPrice">
                  <el-input-number
                    v-model="procurementManagementFeeUnitPrice"
                    :precision="2"
                    :step="0.01"
                    placeholder="请输入采管费总金额"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    合计模式：直接输入总采管费金额，不在每个清单项单独计算（可为负数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
          
          <!-- 固定价产品采管费设置 -->
          <el-row :gutter="20" v-if="hasFixedPriceInSelected()">
            <el-col :span="12">
              <el-form :model="{ fixedPriceProcurementFeeType }" :rules="supplierAuditFormRules" ref="fixedPriceFeeTypeForm" label-width="150px">
                <el-form-item label="固定价采管费类型" prop="fixedPriceProcurementFeeType">
                  <el-radio-group v-model="fixedPriceProcurementFeeType">
                    <el-radio label="FIXED_RATE">比率模式</el-radio>
                    <el-radio label="FIXED_UNIT_PRICE">金额模式</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
          
          <!-- 固定价采管费模式选择 -->
          <el-row :gutter="20" v-if="hasFixedPriceInSelected() && fixedPriceProcurementFeeType">
            <el-col :span="12">
              <el-form :model="{ fixedPriceProcurementFeeMode }" :rules="supplierAuditFormRules" ref="fixedPriceFeeModeForm" label-width="150px">
                <el-form-item label="固定价采管费模式" prop="fixedPriceProcurementFeeMode">
                  <el-radio-group v-model="fixedPriceProcurementFeeMode">
                    <el-radio label="UNIT_PRICE">单价模式</el-radio>
                    <el-radio label="TOTAL_AMOUNT">合计模式</el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
          
          <el-row :gutter="20" v-if="hasFixedPriceInSelected() && fixedPriceProcurementFeeType && fixedPriceProcurementFeeMode">
            <!-- 比率模式 + 单价模式 -->
            <el-col v-if="fixedPriceProcurementFeeType === 'FIXED_RATE' && fixedPriceProcurementFeeMode === 'UNIT_PRICE'" :span="12">
              <el-form :model="{ fixedPriceProcurementFeeRate }" :rules="supplierAuditFormRules" ref="fixedPriceFeeRateForm" label-width="150px">
                <el-form-item label="固定价采管费（%）" prop="fixedPriceProcurementFeeRate">
                  <el-input-number
                    v-model="fixedPriceProcurementFeeRate"
                    :precision="2"
                    :step="0.01"
                    :min="-100"
                    :max="100"
                    placeholder="请输入采管费百分比"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    单价模式：每个清单项的采管费单价 = 含税单价 × 比率%（范围：-100到100，支持两位小数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
            
            <!-- 比率模式 + 合计模式 -->
            <el-col v-if="fixedPriceProcurementFeeType === 'FIXED_RATE' && fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT'" :span="12">
              <el-form :model="{ fixedPriceProcurementFeeRate }" :rules="supplierAuditFormRules" ref="fixedPriceFeeRateForm" label-width="150px">
                <el-form-item label="固定价采管费（%）" prop="fixedPriceProcurementFeeRate">
                  <el-input-number
                    v-model="fixedPriceProcurementFeeRate"
                    :precision="2"
                    :step="0.01"
                    :min="-100"
                    :max="100"
                    placeholder="请输入采管费百分比"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    合计模式：总采管费 = 结算总金额 × 比率%（范围：-100到100，支持两位小数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
            
            <!-- 金额模式 + 单价模式 -->
            <el-col v-if="fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE' && fixedPriceProcurementFeeMode === 'UNIT_PRICE'" :span="12">
              <el-form :model="{ fixedPriceProcurementFeeUnitPrice }" :rules="supplierAuditFormRules" ref="fixedPriceFeeUnitPriceForm" label-width="150px">
                <el-form-item label="固定价采管费单价" prop="fixedPriceProcurementFeeUnitPrice">
                  <el-input-number
                    v-model="fixedPriceProcurementFeeUnitPrice"
                    :precision="5"
                    :step="0.00001"
                    placeholder="请输入采管费单价"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    单价模式：每个清单项的采管费金额 = 采管费单价 × 结算数量（可为负数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
            
            <!-- 金额模式 + 合计模式 -->
            <el-col v-if="fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE' && fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT'" :span="12">
              <el-form :model="{ fixedPriceProcurementFeeUnitPrice }" :rules="supplierAuditFormRules" ref="fixedPriceFeeUnitPriceForm" label-width="150px">
                <el-form-item label="固定价采管费金额" prop="fixedPriceProcurementFeeUnitPrice">
                  <el-input-number
                    v-model="fixedPriceProcurementFeeUnitPrice"
                    :precision="2"
                    :step="0.01"
                    placeholder="请输入采管费总金额"
                    style="width: 100%;"
                  ></el-input-number>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    合计模式：直接输入总采管费金额，不在每个清单项单独计算（可为负数）
                  </div>
                </el-form-item>
              </el-form>
            </el-col>
          </el-row>
        </div>

        <!-- 供应商审核选择 -->
        <div class="supplier-audit-section">
          <div class="section-title">供应商审核设置</div>
          <el-form :model="supplierAuditForm" :rules="supplierAuditFormRules" ref="supplierAuditForm" label-width="150px">
            <el-form-item label="是否需要审核" prop="needSupplierAudit">
              <el-radio-group v-model="supplierAuditForm.needSupplierAudit">
                <el-radio :label="false">不需要审核</el-radio>
                <el-radio :label="true">需要审核</el-radio>
              </el-radio-group>
              <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                <div v-if="!supplierAuditForm.needSupplierAudit">
                  选择"不需要审核"：结算单创建后状态为"待确认"，需求方可以直接看到并进行确认
                </div>
                <div v-else>
                  选择"需要审核"：结算单创建后状态为"草稿"，只对供应商可见，供应商审核通过后需求方才能看到并确认
                </div>
              </div>
            </el-form-item>
            <el-form-item v-if="supplierAuditForm.needSupplierAudit" label="选择供应商审核人" prop="supplierAuditorId">
              <el-select 
                v-model="supplierAuditForm.supplierAuditorId" 
                placeholder="请选择供应商审核人" 
                style="width: 100%" 
                filterable
                :loading="supplierAuditorListLoading"
                loading-text="正在查询审核人..."
              >
                <el-option
                  v-for="user in supplierAuditorList"
                  :key="user.id"
                  :label="`${user.realName || user.username} (${user.unitName || ''})`"
                  :value="user.id">
                </el-option>
              </el-select>
            </el-form-item>
          </el-form>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="confirmDialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          v-permission="'settlement:create-settlement:create'"
          @click="submitConfirmForm"
          :loading="submitLoading"
        >
          确认提交结算
        </el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { query_settlement_products, create_settlement } from '@/api/modules/settlement'
import { get_order_detail } from '@/api/modules/orders'
import { 
  getUsersByUnitId,
  queryUsersPagination
} from '@/api/modules/users'
import { getUserInfo } from '@/utils/auth'
import { buildPrintDocumentKey, installPlatformPrintFooter } from '@/utils/print-footer'

export default {
  name: 'CreateSettlementDemo',
  data() {
    return {
      loading: false,
      submitLoading: false,
      
      // 确认弹窗状态
      confirmDialogVisible: false,
      // 确认弹窗中的结算单标题（自动生成，用户可编辑）
      confirmSettlementTitle: '',
      
      // 搜索参数
      searchParams: {
        productName: '',
        demandParty: '',
        secondaryUnit: '',
        orderNumber: ''
      },

      // 结算状态Tab：默认显示未结算产品
      activeSettlementTab: 'UNSETTLED',
      
      // 分页参数
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      
      // 产品列表（从API获取）
      products: [],
      
      // 当前选中的定价方式Tab
      activePricingTab: 'FIXED', // 默认选中固定价
      
      // 结算日期选择
      settlementDate: null, // 结算日期，格式：YYYY-MM-DD
      
      // 变动价采管费相关
      procurementManagementFeeType: null, // 变动价采管费类型：FIXED_RATE（比率模式）或 FIXED_UNIT_PRICE（金额模式）
      procurementManagementFeeMode: null, // 变动价采管费模式：UNIT_PRICE（单价模式）或 TOTAL_AMOUNT（合计模式）
      procurementManagementFeeRate: null, // 变动价采管费比率
      procurementManagementFeeUnitPrice: null, // 变动价采管费单价（单价模式）或总金额（合计模式）
      
      // 固定价采管费相关
      fixedPriceProcurementFeeType: null, // 固定价采管费类型：FIXED_RATE（比率模式）或 FIXED_UNIT_PRICE（金额模式）
      fixedPriceProcurementFeeMode: null, // 固定价采管费模式：UNIT_PRICE（单价模式）或 TOTAL_AMOUNT（合计模式）
      fixedPriceProcurementFeeRate: null, // 固定价采管费比率
      fixedPriceProcurementFeeUnitPrice: null, // 固定价采管费单价（单价模式）或总金额（合计模式）
      
      // 供应商审核相关
      supplierAuditForm: {
        needSupplierAudit: false,
        supplierAuditorId: null
      },
      supplierAuditFormRules: {
        settlementDate: [
          { required: true, message: '请选择结算日期', trigger: 'change' }
        ],
        procurementManagementFeeType: [
          {
            validator: (rule, value, callback) => {
              // 采管费类型为可选，不需要必填验证
              callback();
            },
            trigger: 'change'
          }
        ],
        procurementManagementFeeMode: [
          {
            validator: (rule, value, callback) => {
              // 只有当选择了变动价产品且选择了采管费类型时，模式才必填
              if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeType) {
                if (!value) {
                  callback(new Error('请选择变动价采管费模式'));
                } else {
                  callback();
                }
              } else {
                // 如果没有选择采管费类型，模式也为可选
                callback();
              }
            },
            trigger: 'change'
          }
        ],
        procurementManagementFeeRate: [
          {
            validator: (rule, value, callback) => {
              // 只有当选择了变动价产品且类型为比率模式且选择了模式时才需要验证
              if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeType === 'FIXED_RATE' && this.procurementManagementFeeMode) {
                if (value === null || value === undefined || value === '') {
                  callback(new Error('比率模式必须填写采管费（%）'));
                } else if (value < -100) {
                  callback(new Error('采管费（%）不能小于-100'));
                } else if (value > 100) {
                  callback(new Error('采管费（%）不能超过100'));
                } else {
                  callback();
                }
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ],
        procurementManagementFeeUnitPrice: [
          {
            validator: (rule, value, callback) => {
              // 只有当选择了变动价产品且类型为金额模式且选择了模式时才需要验证
              if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeType === 'FIXED_UNIT_PRICE' && this.procurementManagementFeeMode) {
                if (value === null || value === undefined || value === '') {
                  callback(new Error('金额模式必须填写采管费' + (this.procurementManagementFeeMode === 'UNIT_PRICE' ? '单价' : '金额')));
                } else {
                  callback();
                }
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ],
        needSupplierAudit: [
          { required: true, message: '请选择是否需要审核', trigger: 'change' }
        ],
        supplierAuditorId: [
          { 
            validator: (rule, value, callback) => {
              if (this.supplierAuditForm.needSupplierAudit && !value) {
                callback(new Error('请选择供应商审核人'));
              } else {
                callback();
              }
            }, 
            trigger: 'change' 
          }
        ],
        fixedPriceProcurementFeeType: [
          {
            validator: (rule, value, callback) => {
              // 固定价采管费类型为可选，不需要必填验证
              callback();
            },
            trigger: 'change'
          }
        ],
        fixedPriceProcurementFeeMode: [
          {
            validator: (rule, value, callback) => {
              // 只有当选择了固定价产品且选择了采管费类型时，模式才必填
              if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType) {
                if (!value) {
                  callback(new Error('请选择固定价采管费模式'));
                } else {
                  callback();
                }
              } else {
                // 如果没有选择采管费类型，模式也为可选
                callback();
              }
            },
            trigger: 'change'
          }
        ],
        fixedPriceProcurementFeeRate: [
          {
            validator: (rule, value, callback) => {
              // 只有当选择了固定价产品且类型为比率模式时才需要验证
              if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType === 'FIXED_RATE' && this.fixedPriceProcurementFeeMode) {
                if (value === null || value === undefined || value === '') {
                  callback(new Error('比率模式必须填写采管费（%）'));
                } else if (value < -100) {
                  callback(new Error('采管费（%）不能小于-100'));
                } else if (value > 100) {
                  callback(new Error('采管费（%）不能超过100'));
                } else {
                  callback();
                }
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ],
        fixedPriceProcurementFeeUnitPrice: [
          {
            validator: (rule, value, callback) => {
              // 只有当选择了固定价产品且类型为金额模式时才需要验证
              if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE' && this.fixedPriceProcurementFeeMode) {
                if (value === null || value === undefined || value === '') {
                  callback(new Error('金额模式必须填写采管费' + (this.fixedPriceProcurementFeeMode === 'UNIT_PRICE' ? '单价' : '金额')));
                } else {
                  callback();
                }
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ]
      },
      
      // 供应商审核人列表
      supplierAuditorList: [],
      
      // 供应商审核人列表加载状态
      supplierAuditorListLoading: false,
      
      // 当前用户信息
      currentUser: null,
      
      // 存储所有已选择的产品信息（跨分页保持选择状态）
      // key: 产品ID (shipmentListId), value: { selected: boolean, settlement_quantity: number, taxInclusiveFloatingPrice: number }
      selectedProductsMap: new Map(),
      // 响应式计数器，用于触发计算属性更新（Vue 2 中 Map 不是响应式的）
      selectedProductsMapUpdateTrigger: 0
    };
  },
  
  computed: {
    // 当前页的产品列表（API已经处理了过滤和分页）
    filteredProducts() {
      return this.products;
    },
    
    // 已选产品数量（基于所有已选择的产品，不仅仅是当前页）
    selectedProductsCount() {
      // Map 中只存储了 selected === true && settlement_quantity !== 0 的产品
      // 使用响应式计数器来触发更新（Vue 2 中 Map 不是响应式的）
      const count = this.selectedProductsMap.size;
      // 访问响应式计数器，确保计算属性能够检测到变化
      void this.selectedProductsMapUpdateTrigger;
      return count;
    },
    
    // 结算总金额 = 含税金额合计（与确认弹窗表格中“含税金额”列一致，为各产品含税金额之和）
    settlementTotalAmount() {
      // 依赖响应式计数器，确保 Map 变化时重新计算
      void this.selectedProductsMapUpdateTrigger;
      // 与 getSelectedProductsList() 同源，确保和表格每行含税金额一致
      const selectedProducts = this.getSelectedProductsList();
      
      if (selectedProducts.length === 0) {
        return 0;
      }
      
      // 含税金额合计：对每个已选产品按含税规则计算金额并求和
      let total = 0;
      selectedProducts.forEach((item) => {
        total += this.calculateProductSettlementAmount(item);
      });
      return total;
    },
    
    // 是否全选（基于当前页）
    allSelected() {
      const selectableProducts = this.filteredProducts.filter(item => item.canSelect);
      return selectableProducts.length > 0 && 
             selectableProducts.every(item => item.selected && item.settlement_quantity !== 0);
    },
    
    // 是否显示采管费列（合计模式时隐藏，且必须采管费大于0才显示）
    showProcurementManagementFeeColumns() {
      // 如果有固定价产品且是合计模式，隐藏采管费列
      if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT') {
        return false;
      }
      // 如果有变动价产品且是合计模式，隐藏采管费列
      if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        return false;
      }
      
      // 检查是否有采管费配置
      if (!this.hasProcurementManagementFee()) {
        return false;
      }
      
      // 检查是否有任何已选择产品的采管费不等于0（包括负数）
      const selectedProducts = Array.from(this.selectedProductsMap.values());
      const hasNonZeroFee = selectedProducts.some(product => {
        const fee = this.calculateProcurementManagementFee(product);
        return fee !== 0; // 不等于0时显示（包括正数和负数）
      });
      
      // 如果是单价模式，检查是否有采管费单价不等于0
      let hasNonZeroUnitPrice = false;
      if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeMode === 'UNIT_PRICE') {
        hasNonZeroUnitPrice = selectedProducts.some(product => {
          if (product.pricingMethod === 'FIXED' || !product.pricingMethod) {
            const unitPrice = this.calculateProcurementManagementFeeUnitPrice(product);
            return unitPrice !== 0;
          }
          return false;
        });
      }
      if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeMode === 'UNIT_PRICE') {
        hasNonZeroUnitPrice = selectedProducts.some(product => {
          if (product.pricingMethod === 'FLOATING') {
            const unitPrice = this.calculateProcurementManagementFeeUnitPrice(product);
            return unitPrice !== 0;
          }
          return false;
        });
      }
      
      // 只有当有非零采管费时才显示列（包括正数和负数）
      return hasNonZeroFee || hasNonZeroUnitPrice;
    },
    
    // 是否显示实际单价列（只在固定价产品且采管费不为0时显示）
    showActualUnitPriceColumn() {
      // 只显示固定价产品
      if (!this.hasFixedPriceInSelected() || this.hasFloatingPriceInSelected()) {
        return false;
      }
      // 必须有采管费配置
      if (!this.hasProcurementManagementFee()) {
        return false;
      }
      // 合计模式不显示实际单价列（实际单价列只用于单价模式）
      if (this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT') {
        return false;
      }
      // 单价模式时，检查是否配置了采管费
      return true;
    }
  },
  
  watch: {
    // 监听产品数据变化，更新全选状态
    products: {
      handler() {
        // 当产品数据变化时，可以在这里处理一些逻辑
      },
      deep: true
    },
    
    // 监听是否需要供应商审核的选择变化
    'supplierAuditForm.needSupplierAudit'(newVal) {
      if (newVal === true) {
        // 当选择需要供应商审核时，查询供应商审核人列表
        this.getSupplierAuditorList();
      } else {
        // 当选择不需要供应商审核时，清空审核人选择
        this.supplierAuditForm.supplierAuditorId = null;
        this.supplierAuditorList = [];
      }
    },
    
    // 监听变动价采管费类型变化
    procurementManagementFeeType() {
      // 类型变化时清空模式选择
      this.procurementManagementFeeMode = null;
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听变动价采管费模式变化
    procurementManagementFeeMode() {
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听变动价采管费比率变化，强制更新视图
    procurementManagementFeeRate() {
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听变动价采管费单价变化，强制更新视图
    procurementManagementFeeUnitPrice() {
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听固定价采管费类型变化
    fixedPriceProcurementFeeType() {
      // 类型变化时清空模式选择
      this.fixedPriceProcurementFeeMode = null;
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听固定价采管费模式变化
    fixedPriceProcurementFeeMode() {
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听固定价采管费比率变化
    fixedPriceProcurementFeeRate() {
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听固定价采管费单价变化
    fixedPriceProcurementFeeUnitPrice() {
      if (this.confirmDialogVisible) {
        this.$forceUpdate();
      }
    },
    
    // 监听确认弹窗打开，确保结算总金额正确计算
    confirmDialogVisible(newVal) {
      if (newVal) {
        // 弹窗打开时，强制更新视图，确保结算总金额正确计算
        this.$nextTick(() => {
          this.$forceUpdate();
});
      }
    }
  },
  
  mounted() {
    // 获取当前用户信息
    this.getCurrentUserInfo();
    // 页面加载时获取数据
    this.fetchSettlementProducts();
  },
  
  methods: {
    // 获取规格型号文本
    getSpecificationText(item) {
      if (!item) return '';
      return (
        item.specification ||
        item.materialsSpec ||
        item.itemSpecification ||
        item.materialRequirementSpec ||
        ''
      );
    },

    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 获取结算产品数据
    async fetchSettlementProducts() {
      try {
        this.loading = true;
        
        const params = {
          page: this.pagination.current - 1, // API使用从0开始的页码
          size: this.pagination.pageSize,
          sort: 'shipmentDate,desc'
        };
        
        // 添加搜索条件
        if (this.searchParams.productName) {
          params.productName = this.searchParams.productName;
        }
        if (this.searchParams.demandParty) {
          params.requesterName = this.searchParams.demandParty;
        }
        if (this.searchParams.secondaryUnit) {
          params.secondaryUnitName = this.searchParams.secondaryUnit;
        }
        if (this.searchParams.orderNumber) {
          params.orderNumber = this.searchParams.orderNumber;
        }

        // 添加结算状态过滤条件
        if (this.activeSettlementTab && this.activeSettlementTab !== 'ALL') {
          params.settlementStatus = this.activeSettlementTab;
        }
        
        // 添加定价方式过滤条件
        if (this.activePricingTab) {
          params.pricingMethod = this.activePricingTab;
        }
        
        const response = await query_settlement_products(params);
        
if (response.success && response.data && response.data.content) {
          // 转换API数据格式为组件需要的格式
          this.products = response.data.content.map(item => {
            const productId = item.shipmentListId;
            // 检查该产品是否在已选择的产品映射中
            const selectedInfo = this.selectedProductsMap.get(productId);
            
            return {
              id: productId,
              orderId: item.orderId, // 订单ID（数字）
              orderNumber: item.orderNumber, // 订单编号（字符串）
              orderItemId: item.orderItemId, // 订单项目ID
              demandParty: item.requesterName, // 需求方名称
              secondaryUnit: item.secondaryUnitName, // 二级单位名称
              productName: item.productName, // 产品名称
              specification: item.specification, // 规格型号
              materialRequirement: item.materialRequirement, // 材质要求
              shippedQuantity: item.shippedQuantity, // 发货数量
              remainingQuantity: item.remainingQuantity, // 剩余数量
              unit: item.unit, // 单位
              taxInclusiveUnitPrice: item.taxInclusiveUnitPrice, // 含税单价
              taxExclusiveUnitPrice: item.taxExclusiveUnitPrice, // 不含税单价
              taxInclusiveFloatingPrice: selectedInfo ? selectedInfo.taxInclusiveFloatingPrice : (item.taxInclusiveFloatingPrice || 0), // 含税变动价（可编辑），优先使用已保存的值
              taxInclusiveBasePrice: item.taxInclusiveBasePrice || null, // 含税固定价（从API获取，不可修改）
              taxInclusiveTotalAmount: item.taxInclusiveTotalAmount, // 含税总金额
              pricingMethod: item.pricingMethod, // 定价方式
              taxRate: item.taxRate, // 税率
              shipmentDate: item.shipmentDate, // 发货日期
              logisticsInfoId: item.logisticsInfoId, // 物流信息ID
              trackingNumber: item.trackingNumber, // 运单号
              productSupplierId: item.productSupplierId, // 产品供应商ID
              productSupplierName: item.productSupplierName, // 产品供应商名称
              demandUnitIds: item.demandUnitIds, // 需求方单位ID列表
              selected: selectedInfo ? selectedInfo.selected : false, // 恢复选择状态
              settlement_quantity: selectedInfo ? selectedInfo.settlement_quantity : 0, // 恢复结算数量
              canSelect: (item.remainingQuantity || 0) !== 0 || (item.shippedQuantity || 0) < 0 // 修改选择逻辑：剩余数量不为0或发货数量为负数（退货）即可选择
            };
          });
          
          // 更新分页信息
          this.pagination.total = response.data.totalElements;
          
          this.$message.success(`成功获取 ${this.products.length} 条产品数据`);
        } else {
          console.error('API响应格式错误:', response);
          this.$message.error('获取产品数据失败: 响应格式错误');
          this.products = [];
        }
      } catch (error) {
        console.error('获取结算产品数据失败:', error);
        this.$message.error('获取产品数据失败: ' + (error.message || '网络错误'));
        this.products = [];
      } finally {
        this.loading = false;
      }
    },
    
    // 搜索
    handleSearch() {
      // 搜索时重置到第一页
      this.pagination.current = 1;
      // 调用API获取数据
      this.fetchSettlementProducts();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchParams = {
        productName: '',
        demandParty: '',
        secondaryUnit: '',
        orderNumber: ''
      };
      // 重置时回到第一页
      this.pagination.current = 1;
      // 重新获取数据
      this.fetchSettlementProducts();
    },
    
    // 判断产品是否已结算（剩余数量为0且非退货）
    isProductSettled(product) {
      if (!product) return false;
      const remainingQuantity = Number(product.remainingQuantity || 0);
      const shippedQuantity = Number(product.shippedQuantity || 0);
      return remainingQuantity === 0 && shippedQuantity >= 0;
    },

    // 已结算产品行样式
    getProductRowClassName({ row }) {
      return this.isProductSettled(row) ? 'settled-product-row' : '';
    },

    // 处理结算状态Tab切换
    handleSettlementTabChange() {
      this.pagination.current = 1;
      this.fetchSettlementProducts();
    },

    // 处理定价方式Tab切换
    handlePricingTabChange() {
      // 切换tab时重置到第一页
      this.pagination.current = 1;
      // 清空已选择产品映射（切换定价方式时应该清空所有选择）
      this.selectedProductsMap.clear();
      // 更新响应式计数器
      this.selectedProductsMapUpdateTrigger = 0;
      // 清空当前产品选择状态
      this.products.forEach(product => {
        product.selected = false;
        product.settlement_quantity = 0;
      });
      // 重新获取数据
      this.fetchSettlementProducts();
    },
    
    // 页码改变
    handleCurrentChange(currentPage) {
      this.pagination.current = currentPage;
      this.fetchSettlementProducts();
    },
    
    // 每页条数改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.current = 1; // 改变每页条数时回到第一页
      this.fetchSettlementProducts();
    },
    
    // 全选/取消全选（仅对当前页）
    toggleSelectAll() {
      const shouldSelect = !this.allSelected;
      
      this.filteredProducts.forEach(item => {
        // 只对可以结算的产品进行操作
        if (item.canSelect) {
          item.selected = shouldSelect;
          if (shouldSelect) {
            // 选中时设置默认结算数量：退货记录使用发货数量，正常记录使用剩余数量
            if (item.shippedQuantity < 0) {
              item.settlement_quantity = Number(item.shippedQuantity) || 0;
            } else {
              item.settlement_quantity = Number(item.remainingQuantity) || 0;
            }
          } else {
            item.settlement_quantity = 0;
          }
          // 更新已选择产品映射
          this.updateSelectedProductsMap(item);
        }
      });
    },
    
    // 处理产品选择变化
    handleProductSelectionChange(product) {
      // 如果剩余数量为0，不允许选择
      if (!product.canSelect) {
        product.selected = false;
        product.settlement_quantity = 0;
        // 从已选择映射中移除
        this.selectedProductsMap.delete(product.id);
        // 更新响应式计数器
        this.selectedProductsMapUpdateTrigger = this.selectedProductsMap.size;
        this.$message.warning(`产品 ${product.productName} 剩余数量为0，无法进行结算`);
        return;
      }
      
      if (product.selected) {
        // 选中时设置默认结算数量：退货记录使用发货数量，正常记录使用剩余数量
        if (product.shippedQuantity < 0) {
          product.settlement_quantity = Number(product.shippedQuantity) || 0;
        } else {
          product.settlement_quantity = Number(product.remainingQuantity) || 0;
        }
} else {
        // 取消选中时清零结算数量
        product.settlement_quantity = 0;
}
      
      // 更新已选择产品映射
      this.updateSelectedProductsMap(product);
    },
    
    // 更新已选择产品映射
    updateSelectedProductsMap(product) {
if (product.selected && product.settlement_quantity !== 0) {
        // 保存完整的产品信息（用于跨分页保持选择状态和提交结算）
        this.selectedProductsMap.set(product.id, {
          ...product, // 保存完整的产品信息
          selected: product.selected,
          settlement_quantity: product.settlement_quantity,
          taxInclusiveFloatingPrice: product.taxInclusiveFloatingPrice || 0
        });
} else {
        // 取消选择时从映射中移除
        this.selectedProductsMap.delete(product.id);
}
      
      // 更新响应式计数器，触发计算属性重新计算
      this.selectedProductsMapUpdateTrigger = this.selectedProductsMap.size;
    },
    
    // 处理数量变化
    handleQuantityChange(product) {
      // 确保数量在合理范围内
      const maxQuantity = Number(product.remainingQuantity);
      const minQuantity = product.shippedQuantity < 0 ? product.shippedQuantity : (maxQuantity < 0 ? maxQuantity : 0);
      
      if (Number(product.settlement_quantity) > maxQuantity) {
        product.settlement_quantity = maxQuantity;
        this.$message.warning(`结算数量不能超过剩余数量 ${maxQuantity}`);
      }
      if (Number(product.settlement_quantity) < minQuantity) {
        product.settlement_quantity = minQuantity;
        this.$message.warning(`结算数量不能小于 ${minQuantity}`);
      }
      
      // 更新已选择产品映射
      this.updateSelectedProductsMap(product);
    },
    
    // 计算产品结算金额（含税）
    // 固定价产品且有采管费时，使用实际单价计算；否则使用原逻辑
    calculateProductSettlementAmount(product) {
      // 从 Map 中获取的产品都是已选择的，所以只检查 settlement_quantity
      if (!product.settlement_quantity || product.settlement_quantity === 0) {
return 0;
      }
      
      // 固定价产品且有采管费时，使用实际单价计算含税金额
      if (product.pricingMethod === 'FIXED' || !product.pricingMethod) {
        if (this.hasProcurementManagementFee()) {
          const actualUnitPrice = this.calculateActualUnitPrice(product); // 已统一为五位小数
          const amount = actualUnitPrice * (product.settlement_quantity || 0);
          // 含税金额也四舍五入到2位小数
          const result = Math.round(amount * 100) / 100;
return result;
        }
        // 固定价产品但没有采管费时，使用含税单价
        const unitPrice = product.taxInclusiveUnitPrice || 0;
        const result = unitPrice * (product.settlement_quantity || 0);
return result;
      }
      
      // 变动价产品：使用原逻辑（基础价 + 变动价）
      if (product.pricingMethod === 'FLOATING') {
        const unitPrice = this.calculateFloatingSettlementPrice(product);
        const result = unitPrice * (product.settlement_quantity || 0);
return result;
      }
      
return 0;
    },
    
    // 计算采管费单价
    calculateProcurementManagementFeeUnitPrice(product) {
      let unitPrice = 0;
      
      if (product.pricingMethod === 'FLOATING') {
        // 变动价产品采管费
        if (!this.procurementManagementFeeType || !this.procurementManagementFeeMode) return 0;
        
        // 合计模式不在清单项计算采管费
        if (this.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
          return 0;
        }
        
        // 单价模式才计算每个清单项的采管费
        if (this.procurementManagementFeeMode === 'UNIT_PRICE') {
          if (this.procurementManagementFeeType === 'FIXED_RATE') {
            // 比率模式：采管费单价 = (基础价 + 变动价) * 比率%
            const basePrice = product.taxInclusiveBasePrice || 0;
            const floatingPrice = product.taxInclusiveFloatingPrice || 0;
            const rateDecimal = (this.procurementManagementFeeRate || 0) / 100;
            unitPrice = (basePrice + floatingPrice) * rateDecimal;
          } else if (this.procurementManagementFeeType === 'FIXED_UNIT_PRICE') {
            unitPrice = this.procurementManagementFeeUnitPrice || 0;
          }
        }
      } else {
        // 固定价产品采管费
        if (!this.fixedPriceProcurementFeeType || !this.fixedPriceProcurementFeeMode) return 0;
        
        // 合计模式不在清单项计算采管费
        if (this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT') {
          return 0;
        }
        
        // 单价模式才计算每个清单项的采管费
        if (this.fixedPriceProcurementFeeMode === 'UNIT_PRICE') {
          if (this.fixedPriceProcurementFeeType === 'FIXED_RATE') {
            // 固定比率：采管费单价 = 含税单价 * 比率%
            const taxInclusiveUnitPrice = product.taxInclusiveUnitPrice || 0;
            const rateDecimal = (this.fixedPriceProcurementFeeRate || 0) / 100;
            unitPrice = taxInclusiveUnitPrice * rateDecimal;
          } else if (this.fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE') {
            unitPrice = this.fixedPriceProcurementFeeUnitPrice || 0;
          }
        }
      }
      
      return this.roundUnitPrice(unitPrice);
    },
    
    // 计算采管费金额
    calculateProcurementManagementFee(product) {
      if (!product.selected || product.settlement_quantity === 0) return 0;
      
      // 合计模式不在清单项计算采管费
      if (product.pricingMethod === 'FIXED' && this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT') {
        return 0;
      }
      if (product.pricingMethod === 'FLOATING' && this.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        return 0;
      }
      
      // 采管费金额 = 采管费单价（五位小数） * 结算数量
      const unitPrice = this.calculateProcurementManagementFeeUnitPrice(product);
      return unitPrice * Math.abs(product.settlement_quantity || 0);
    },
    
    // 计算固定价合计模式的采管费总额
    calculateFixedPriceTotalProcurementManagementFee() {
      if (!this.hasFixedPriceInSelected() || !this.fixedPriceProcurementFeeType || !this.fixedPriceProcurementFeeMode) {
        return 0;
      }
      
      // 只有合计模式才计算总采管费
      if (this.fixedPriceProcurementFeeMode !== 'TOTAL_AMOUNT') {
        return 0;
      }
      
      if (this.fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE') {
        // 金额模式：直接使用用户输入的总金额
        return this.fixedPriceProcurementFeeUnitPrice || 0;
      } else if (this.fixedPriceProcurementFeeType === 'FIXED_RATE') {
        // 比率模式：总金额 × 比率%（总金额与确认弹窗结算总金额一致，为清单含税金额合计）
        const totalAmount = this.getConfirmDialogSettlementTotal();
        const rateDecimal = (this.fixedPriceProcurementFeeRate || 0) / 100;
        return totalAmount * rateDecimal;
      }
      
      return 0;
    },
    
    // 计算变动价合计模式的采管费总额
    calculateFloatingPriceTotalProcurementManagementFee() {
      if (!this.hasFloatingPriceInSelected() || !this.procurementManagementFeeType || !this.procurementManagementFeeMode) {
        return 0;
      }
      
      // 只有合计模式才计算总采管费
      if (this.procurementManagementFeeMode !== 'TOTAL_AMOUNT') {
        return 0;
      }
      
      if (this.procurementManagementFeeType === 'FIXED_UNIT_PRICE') {
        // 金额模式：直接使用用户输入的总金额
        return this.procurementManagementFeeUnitPrice || 0;
      } else if (this.procurementManagementFeeType === 'FIXED_RATE') {
        // 比率模式：总金额 × 比率%（总金额与确认弹窗结算总金额一致，为清单含税金额合计）
        const totalAmount = this.getConfirmDialogSettlementTotal();
        const rateDecimal = (this.procurementManagementFeeRate || 0) / 100;
        return totalAmount * rateDecimal;
      }
      
      return 0;
    },
    
    // 计算变动价产品的结算价
    // 单价模式：结算价 = 基础价 + 变动价 + 采管费单价
    // 合计模式：结算价 = 基础价 + 变动价（采管费在总金额层面计算）
    calculateFloatingSettlementPrice(product) {
      if (product.pricingMethod !== 'FLOATING') {
        return product.taxInclusiveUnitPrice || 0;
      }
      
      const basePrice = product.taxInclusiveBasePrice || 0;
      const floatingPrice = product.taxInclusiveFloatingPrice || 0;
      let settlementPrice = basePrice + floatingPrice;
      
      // 如果配置了采管费类型和模式，且为单价模式，结算价需要加上采管费单价
      // 合计模式下，calculateProcurementManagementFeeUnitPrice返回0，所以不会加上采管费
      if (this.procurementManagementFeeType && this.procurementManagementFeeMode) {
        const procurementFeeUnitPrice = this.calculateProcurementManagementFeeUnitPrice(product);
        settlementPrice = settlementPrice + procurementFeeUnitPrice;
      }
      
      return this.roundUnitPrice(settlementPrice);
    },
    
    // 计算实际单价（含税单价 - 采管费单价，仅用于固定价产品）
    calculateActualUnitPrice(product) {
      if (!product.selected || product.settlement_quantity === 0) return 0;
      
      // 只计算固定价产品的实际单价
      if (product.pricingMethod !== 'FIXED' && product.pricingMethod !== null && product.pricingMethod !== undefined) {
        return 0;
      }
      
      // 固定价产品：使用含税单价
      const taxInclusiveUnitPrice = product.taxInclusiveUnitPrice || 0;
      
      // 计算采管费单价，并统一为五位小数
      const procurementFeeUnitPrice = this.calculateProcurementManagementFeeUnitPrice(product);
      return this.roundUnitPrice(taxInclusiveUnitPrice - procurementFeeUnitPrice);
    },
    
    // 判断是否有采管费（用于控制实际单价列的显示）
    hasProcurementManagementFee() {
      // 变动价产品：检查是否配置了采管费类型和模式
      if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeType && this.procurementManagementFeeMode) {
        // 合计模式时，直接返回true（如果配置了类型和模式就认为有采管费，避免循环调用）
        if (this.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
          return true;
        }
        // 单价模式时，检查是否配置了采管费
        return true;
      }
      
      // 固定价产品：检查是否配置了采管费类型和模式
      if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType && this.fixedPriceProcurementFeeMode) {
        // 合计模式时，直接返回true（如果配置了类型和模式就认为有采管费，避免循环调用）
        if (this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT') {
          return true;
        }
        // 单价模式时，检查是否配置了采管费
        return true;
      }
      
      return false;
    },
    
    // 处理变动价变化
    handleFloatingPriceChange(product) {
      // 如果产品已选中，重新计算结算金额
      if (product.selected) {
        // 更新已选择产品映射中的变动价
        this.updateSelectedProductsMap(product);
        // 触发结算总金额重新计算
        this.$forceUpdate();
      }
    },
    
    // 检查选中的产品中是否有变动价产品
    hasFloatingPriceInSelected() {
      const selectedProducts = Array.from(this.selectedProductsMap.values());
      return selectedProducts.some(product => product.pricingMethod === 'FLOATING');
    },
    
    // 检查选中的产品中是否有固定价产品
    hasFixedPriceInSelected() {
      const selectedProducts = Array.from(this.selectedProductsMap.values());
      return selectedProducts.some(product => product.pricingMethod === 'FIXED' || !product.pricingMethod);
    },
    
    // 跳转到确认页面
    async goToConfirm() {
      if (this.selectedProductsCount === 0) {
        this.$message.warning('请先选择需要结算的产品');
        return;
      }
      
      // 验证变动价产品是否填写了变动价
      // 从 selectedProductsMap 中获取所有已选择的产品（跨分页）
      const selectedProducts = Array.from(this.selectedProductsMap.values());
      const floatingPriceProducts = selectedProducts.filter(product => product.pricingMethod === 'FLOATING');
      
      if (floatingPriceProducts.length > 0) {
        const missingFloatingPriceProducts = floatingPriceProducts.filter(product => 
          product.taxInclusiveFloatingPrice === null || product.taxInclusiveFloatingPrice === undefined
        );
        
        if (missingFloatingPriceProducts.length > 0) {
          const productNames = missingFloatingPriceProducts.map(p => p.productName).join('、');
          this.$message.error(`变动价产品【${productNames}】必须填写变动价才能进行结算`);
          return;
        }
      }
      
      // 初始化结算日期为当前日期
      const now = new Date();
      const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      this.settlementDate = currentDate;
      
      // 初始化采管费相关字段
      this.procurementManagementFeeType = null;
      this.procurementManagementFeeMode = null;
      this.procurementManagementFeeRate = null;
      this.procurementManagementFeeUnitPrice = null;
      this.fixedPriceProcurementFeeType = null;
      this.fixedPriceProcurementFeeMode = null;
      this.fixedPriceProcurementFeeRate = null;
      this.fixedPriceProcurementFeeUnitPrice = null;
      
      // 重置供应商审核表单
      this.supplierAuditForm = {
        needSupplierAudit: false,
        supplierAuditorId: null
      };
      
      // 清空供应商审核人列表
      this.supplierAuditorList = [];
      
      // 根据订单对应合同与结算日期生成默认结算单标题，用户可在弹窗中修改
      try {
        this.confirmSettlementTitle = await this.getDefaultSettlementTitle();
      } catch (e) {
        const dateOnly = this.settlementDate || (this.getSettlementDateString().slice(0, 10));
        this.confirmSettlementTitle = `结算单-${dateOnly}`;
      }
      
      this.confirmDialogVisible = true;
      
      // 强制更新视图，确保结算总金额正确计算
      this.$nextTick(() => {
        // 直接访问计算属性，触发计算
        void this.settlementTotalAmount;
        this.$forceUpdate();
      });
    },
    
    
    // 获取选中的产品列表（跨分页）
    getSelectedProductsList() {
      return Array.from(this.selectedProductsMap.values());
    },

    // 确认弹窗中的「结算总金额」= 结算产品清单中每行「含税金额」相加（不含采管费）
    getConfirmDialogSettlementTotal() {
      const list = this.getSelectedProductsList();
      if (!list || list.length === 0) return 0;
      return list.reduce((sum, row) => sum + this.calculateProductSettlementAmount(row), 0);
    },

    // 结算单主金额口径：产品含税金额 + 采管费。
    // 该金额写入 settlements.amount，供结算单列表展示，避免列表漏计采管费。
    getConfirmDialogSettlementGrandTotal() {
      const baseTotal = this.getConfirmDialogSettlementTotal();
      const list = this.getSelectedProductsList();
      if (!list || list.length === 0) return baseTotal;

      const itemFeeTotal = list.reduce((sum, item) =>
        sum + this.calculateProcurementManagementFee(item), 0);
      let totalModeFee = 0;
      if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT') {
        totalModeFee += this.calculateFixedPriceTotalProcurementManagementFee();
      }
      if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        totalModeFee += this.calculateFloatingPriceTotalProcurementManagementFee();
      }
      return baseTotal + itemFeeTotal + totalModeFee;
    },
    
    /**
     * 根据订单对应合同与结算日期生成默认结算单标题：《合同名称》结算单-YYYY-MM-DD
     * 需查询订单详情获取合同名称；失败或无合同时退回简易标题
     */
    async getDefaultSettlementTitle() {
      const selectedProducts = this.getSelectedProductsList();
      const firstProduct = selectedProducts[0];
      const dateStr = this.settlementDate || this.getSettlementDateString().slice(0, 10); // YYYY-MM-DD
      const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;

      if (!firstProduct || !firstProduct.orderId) {
        return `结算单-${dateOnly}`;
      }

      try {
        const res = await get_order_detail(firstProduct.orderId);
        const body = (res && res.data) ? res.data : res;
        const order = (body && body.data) ? body.data : body;
        const contractName = (order && order.contractDetails && order.contractDetails[0] && order.contractDetails[0].contractName) ||
          (order && order.contractName) ||
          '';
        if (contractName) {
          return `《${contractName}》结算单-${dateOnly}`;
        }
      } catch (e) {
        console.warn('获取订单合同信息失败，使用默认标题:', e);
      }
      return `结算单-${dateOnly}`;
    },

    // 生成结算标题（保留用于兼容，实际提交使用 confirmSettlementTitle）
    generateSettlementTitle(selectedProducts) {
      const dateStr = this.settlementDate || this.getSettlementDateString().slice(0, 10);
      const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const firstProduct = selectedProducts && selectedProducts[0];
      if (!firstProduct) return `结算单-${dateOnly}`;
      return `《未知合同》结算单-${dateOnly}`;
    },
    
    // 从产品列表中获取供应商信息
    getSupplierInfoFromProducts(selectedProducts) {
      // 检查所有产品是否来自同一个供应商
      const supplierIds = [...new Set(selectedProducts.map(item => item.productSupplierId).filter(id => id))];
      const supplierNames = [...new Set(selectedProducts.map(item => item.productSupplierName).filter(name => name))];
      
if (supplierIds.length === 0) {
        console.warn('无法获取任何供应商ID，使用默认值');
        return {
          id: 1,
          unitName: '未知供应商'
        };
      }
      
      if (supplierIds.length === 1) {
        // 所有产品来自同一个供应商
        const supplierId = supplierIds[0];
        const supplierName = supplierNames[0] || '未知供应商';
        
return {
          id: supplierId,
          unitName: supplierName
        };
      } else {
        // 产品来自多个供应商，使用第一个供应商的信息
        const supplierId = supplierIds[0];
        const supplierName = supplierNames[0] || '未知供应商';
        
        console.warn(`产品来自多个供应商 (${supplierIds.length}个)，使用第一个供应商信息:`, { id: supplierId, unitName: supplierName });
        console.warn('所有供应商ID:', supplierIds);
        console.warn('所有供应商名称:', supplierNames);
        
        return {
          id: supplierId,
          unitName: supplierName
        };
      }
    },
    
    // 从产品或关联订单中获取需求方单位ID
    async getDemandUnitIdsFromProducts(selectedProducts) {
      const demandUnitIdsSet = new Set();
      const orderIdsToFetch = new Set();

      selectedProducts.forEach(product => {
        if (product.demandUnitIds && Array.isArray(product.demandUnitIds) && product.demandUnitIds.length > 0) {
          product.demandUnitIds.forEach(id => {
            if (id !== null && id !== undefined) {
              demandUnitIdsSet.add(id);
            }
          });
        } else if (product.orderId) {
          orderIdsToFetch.add(product.orderId);
        }
      });

      for (const orderId of orderIdsToFetch) {
        try {
          const res = await get_order_detail(orderId);
          const order = res.data?.data || res.data;
          if (order?.demandUnitIds?.length) {
            order.demandUnitIds.forEach(id => {
              if (id !== null && id !== undefined) {
                demandUnitIdsSet.add(id);
              }
            });
          } else if (order?.contractDetails?.length) {
            order.contractDetails.forEach(contract => {
              if (contract.demandUnitIds?.length) {
                contract.demandUnitIds.forEach(id => {
                  if (id !== null && id !== undefined) {
                    demandUnitIdsSet.add(id);
                  }
                });
              } else if (contract.demandUnits?.length) {
                contract.demandUnits.forEach(unit => {
                  if (unit.id) {
                    demandUnitIdsSet.add(unit.id);
                  }
                });
              }
            });
          }
        } catch (error) {
          console.error(`获取订单 ${orderId} 需求单位失败:`, error);
        }
      }

      if (demandUnitIdsSet.size === 0) {
        throw new Error('无法获取需求方单位信息，请确认订单已关联需求单位');
      }

      return Array.from(demandUnitIdsSet);
    },
    
    // 获取本地日期时间字符串
    getLocalDateTimeString() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    },
    
    // 获取结算日期字符串（基于用户选择的结算日期，时间取 00:00:00）
    getSettlementDateString() {
      if (!this.settlementDate) {
        // 如果没有选择结算日期，使用当前日期时间
        return this.getLocalDateTimeString();
      }
      // 使用所选日期的 00:00:00 作为结算日期时间
      return `${this.settlementDate}T00:00:00`;
    },
    
    // 获取当前用户信息
    getCurrentUserInfo() {
      try {
        // 使用auth.js中的getUserInfo函数
        const userInfo = getUserInfo();
if (userInfo) {
          this.currentUser = userInfo;
} else {
          console.warn('未能获取用户信息');
          this.$message.error('未能获取用户信息，请重新登录');
        }
      } catch (err) {
        console.error('获取用户信息出错:', err);
        this.$message.error('获取用户信息失败');
      }
    },
    
    // 获取供应商审核人列表
    async getSupplierAuditorList() {
      try {
        this.supplierAuditorListLoading = true;
        
        if (!this.currentUser) {
          console.warn('当前用户信息不存在，无法获取供应商审核人列表');
          this.$message.error('当前用户信息不存在，无法获取供应商审核人列表');
          return;
        }
        
        const roleId = this.currentUser.role?.id || this.currentUser.roleId || this.currentUser.role_id;
        const unitId = this.currentUser.unitId || this.currentUser.unit_id;
        const projectIds = this.currentUser.projectIds || this.currentUser.project_ids;
        const currentUserId = this.currentUser.id || this.currentUser.userId;
        
if (roleId === 2) {
          // 单位用户，获取本单位其他用户
const response = await getUsersByUnitId(unitId);
if (response.data && response.data.success) {
            const allUsers = response.data.data || [];
// 过滤掉当前用户
            this.supplierAuditorList = allUsers.filter(user => {
              const userId = user.id || user.userId;
              const isNotCurrentUser = userId !== currentUserId;
return isNotCurrentUser;
            });
if (this.supplierAuditorList.length === 0) {
              this.$message.warning('本单位没有其他用户可以作为供应商审核人');
            }
          } else {
            console.error('getUsersByUnitId接口调用失败:', response.data);
            this.$message.error('获取单位用户列表失败');
          }
        } else if (roleId === 3) {
          // 项目部用户，获取同项目用户
          if (projectIds && projectIds.length > 0) {
const response = await queryUsersPagination({
              projectIds: projectIds.join(','),
              page: 0,
              size: 100
            });
if (response.data && response.data.success) {
              const allUsers = response.data.data?.content || [];
// 过滤掉当前用户
              this.supplierAuditorList = allUsers.filter(user => {
                const userId = user.id || user.userId;
                const isNotCurrentUser = userId !== currentUserId;
return isNotCurrentUser;
              });
if (this.supplierAuditorList.length === 0) {
                this.$message.warning('同项目没有其他用户可以作为供应商审核人');
              }
            } else {
              console.error('queryUsersPagination接口调用失败:', response.data);
              this.$message.error('获取项目用户列表失败');
            }
          } else {
            console.warn('项目部用户没有项目ID信息');
            this.$message.warning('项目部用户没有项目ID信息');
          }
        } else {
          console.warn('未知的用户角色:', roleId);
          this.$message.warning('未知的用户角色，无法获取供应商审核人列表');
        }
      } catch (error) {
        console.error('获取供应商审核人列表失败:', error);
        this.$message.error('获取供应商审核人列表失败: ' + error.message);
      } finally {
        this.supplierAuditorListLoading = false;
      }
    },
    
    // 提交确认表单
    submitConfirmForm() {
      // 先验证结算日期
      this.$refs.settlementDateForm.validate(dateValid => {
        if (!dateValid) {
          return false;
        }
        
        // 验证逻辑：先验证变动价采管费，再验证固定价采管费，最后验证供应商审核
        const validateFixedPriceFee = () => {
          // 验证供应商审核表单
          this.$refs.supplierAuditForm.validate(auditValid => {
            if (!auditValid) {
              return false;
            }
            
            // 调用确认结算逻辑
            this.confirmSettlement();
          });
        };
        
        const validateFloatingPriceFee = () => {
          // 如果有固定价产品且选择了采管费类型，验证固定价采管费相关表单
          if (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType) {
            // 如果选择了采管费类型，验证模式
            if (this.$refs.fixedPriceFeeModeForm) {
              this.$refs.fixedPriceFeeModeForm.validate(modeValid => {
                if (!modeValid) {
                  return false;
                }
                
                // 根据固定价采管费类型和模式验证对应的表单
                if (this.fixedPriceProcurementFeeType === 'FIXED_RATE') {
                  // 验证固定价采管费比率
                  if (this.$refs.fixedPriceFeeRateForm) {
                    this.$refs.fixedPriceFeeRateForm.validate(rateValid => {
                      if (!rateValid) {
                        return false;
                      }
                      validateFixedPriceFee();
                    });
                  } else {
                    validateFixedPriceFee();
                  }
                } else if (this.fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE') {
                  // 验证固定价采管费单价/金额
                  if (this.$refs.fixedPriceFeeUnitPriceForm) {
                    this.$refs.fixedPriceFeeUnitPriceForm.validate(priceValid => {
                      if (!priceValid) {
                        return false;
                      }
                      validateFixedPriceFee();
                    });
                  } else {
                    validateFixedPriceFee();
                  }
                } else {
                  validateFixedPriceFee();
                }
              });
            } else {
              validateFixedPriceFee();
            }
          } else {
            // 没有选择采管费类型，直接验证供应商审核
            validateFixedPriceFee();
          }
        };
        
        // 如果有变动价产品，验证采管费相关表单（如果选择了采管费类型）
        if (this.hasFloatingPriceInSelected() && this.procurementManagementFeeType) {
          // 如果选择了采管费类型，验证模式
          if (this.$refs.procurementFeeModeForm) {
            this.$refs.procurementFeeModeForm.validate(modeValid => {
              if (!modeValid) {
                return false;
              }
              
              // 根据变动价采管费类型和模式验证对应的表单
              if (this.procurementManagementFeeType === 'FIXED_RATE') {
                // 验证变动价采管费比率
                if (this.$refs.procurementFeeRateForm) {
                  this.$refs.procurementFeeRateForm.validate(rateValid => {
                    if (!rateValid) {
                      return false;
                    }
                    validateFloatingPriceFee();
                  });
                } else {
                  validateFloatingPriceFee();
                }
              } else if (this.procurementManagementFeeType === 'FIXED_UNIT_PRICE') {
                // 验证变动价采管费单价/金额
                if (this.$refs.procurementFeeUnitPriceForm) {
                  this.$refs.procurementFeeUnitPriceForm.validate(priceValid => {
                    if (!priceValid) {
                      return false;
                    }
                    validateFloatingPriceFee();
                  });
                } else {
                  validateFloatingPriceFee();
                }
              } else {
                validateFloatingPriceFee();
              }
            });
          } else {
            validateFloatingPriceFee();
          }
        } else {
          // 没有选择采管费类型，直接验证固定价采管费
          validateFloatingPriceFee();
        }
      });
    },
    
    // 确认结算
    confirmSettlement() {
      this.$confirm('确认提交结算申请？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.submitLoading = true;
        
        try {
          // 构建结算数据
          // 从 selectedProductsMap 中获取所有已选择的产品（跨分页）
          const selectedProducts = Array.from(this.selectedProductsMap.values());
          
          // 获取需求方单位ID列表（从订单/合同推导，不再使用错误默认值）
          const demandUnitIds = await this.getDemandUnitIdsFromProducts(selectedProducts);
          
          // 使用确认弹窗中填写或自动生成的结算单标题（用户可编辑）
          const settlementTitle = (this.confirmSettlementTitle && this.confirmSettlementTitle.trim()) || this.generateSettlementTitle(selectedProducts);
          
          // 获取供应商信息（从选中的产品中获取）
          const supplierInfo = this.getSupplierInfoFromProducts(selectedProducts);
          
          const settlementData = {
            settlementNumber: `JS${new Date().getTime()}`,
            settlementTitle: settlementTitle,
            settlementDate: this.getSettlementDateString(),
            supplierConfirmationDate: this.getLocalDateTimeString(),
            remark: '基于发货记录的结算申请',
            amount: this.getConfirmDialogSettlementGrandTotal(), // 产品含税金额 + 采管费，作为列表展示的结算总额
            needSupplierAudit: this.supplierAuditForm.needSupplierAudit, // 添加供应商审核标志
            supplierAuditorId: this.supplierAuditForm.needSupplierAudit ? this.supplierAuditForm.supplierAuditorId : null, // 添加供应商审核人ID
            // 采管费相关字段（统一使用一组字段，优先使用变动价的配置，其次使用固定价的配置）
            procurementManagementFeeType: this.hasFloatingPriceInSelected() ? this.procurementManagementFeeType : (this.hasFixedPriceInSelected() ? this.fixedPriceProcurementFeeType : null),
            procurementManagementFeeRate: this.hasFloatingPriceInSelected() && this.procurementManagementFeeType === 'FIXED_RATE' 
              ? (this.procurementManagementFeeRate !== null && this.procurementManagementFeeRate !== undefined ? this.procurementManagementFeeRate / 100 : null) 
              : (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType === 'FIXED_RATE' ? (this.fixedPriceProcurementFeeRate !== null && this.fixedPriceProcurementFeeRate !== undefined ? this.fixedPriceProcurementFeeRate / 100 : null) : null),
            procurementManagementFeeUnitPrice: this.hasFloatingPriceInSelected() && this.procurementManagementFeeType === 'FIXED_UNIT_PRICE' 
              ? (this.procurementManagementFeeMode === 'TOTAL_AMOUNT' ? this.calculateFloatingPriceTotalProcurementManagementFee() : this.procurementManagementFeeUnitPrice)
              : (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeMode === 'TOTAL_AMOUNT' ? this.calculateFixedPriceTotalProcurementManagementFee() : (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeType === 'FIXED_UNIT_PRICE' && this.fixedPriceProcurementFeeMode === 'UNIT_PRICE' ? this.fixedPriceProcurementFeeUnitPrice : null)),
            procurementManagementFeeMode: this.hasFloatingPriceInSelected() && this.procurementManagementFeeMode ? this.procurementManagementFeeMode : (this.hasFixedPriceInSelected() && this.fixedPriceProcurementFeeMode ? this.fixedPriceProcurementFeeMode : null),
            demandUnitIds: demandUnitIds, // 使用正确的单位ID数组
            supplier: supplierInfo, // 使用从产品中获取的供应商信息
            logisticsInfoIds: [...new Set(selectedProducts.map(item => item.logisticsInfoId))], // 物流信息ID
            settlementItems: selectedProducts.map(item => {
              // 根据定价方式计算单价和金额
              let unitPrice, actualUnitPrice, settledAmount, taxAmount;
              
              // 判断是否有实际单价（仅固定价产品且有采管费时）
              const hasActualUnitPrice = (item.pricingMethod === 'FIXED' || !item.pricingMethod) && this.hasProcurementManagementFee();
              
              if (hasActualUnitPrice) {
                // 有实际单价时，使用五位小数的实际单价计算含税金额
                actualUnitPrice = this.calculateActualUnitPrice(item);
                const totalAmount = actualUnitPrice * item.settlement_quantity;
                // 含税金额四舍五入到2位小数
                const roundedTotalAmount = Math.round(totalAmount * 100) / 100;
                settledAmount = roundedTotalAmount; // settledAmount 使用实际单价计算的含税金额（四舍五入后）
                taxAmount = roundedTotalAmount * (item.taxRate / 100);
              } else {
                // 没有实际单价时，实际单价为null，按原来的逻辑
                actualUnitPrice = null;
                
                if (item.pricingMethod === 'FLOATING') {
                  // 变动价产品：使用基础价 + 变动价作为单价
                  unitPrice = this.calculateFloatingSettlementPrice(item);
                  settledAmount = item.settlement_quantity * unitPrice;
                  taxAmount = settledAmount * (item.taxRate / 100);
                } else {
                  // 固定价产品：使用原有逻辑
                  unitPrice = item.taxInclusiveUnitPrice;
                  settledAmount = item.settlement_quantity * item.taxInclusiveUnitPrice;
                  taxAmount = item.settlement_quantity * item.taxInclusiveUnitPrice * (item.taxRate / 100);
                }
              }
              
              // 计算采管费
              // 合计模式时，采管费为0（总采管费已存储在procurementManagementFeeUnitPrice中）
              // 其他情况正常计算
              let procurementManagementFee = 0;
              if (item.pricingMethod === 'FLOATING') {
                // 变动价产品：单价模式计算采管费，合计模式为0
                if (this.procurementManagementFeeMode === 'UNIT_PRICE') {
                  procurementManagementFee = this.calculateProcurementManagementFee(item);
                } else {
                  // 合计模式：采管费为0
                  procurementManagementFee = 0;
                }
              } else if (item.pricingMethod === 'FIXED') {
                // 固定价产品：单价模式计算采管费，合计模式为0
                if (this.fixedPriceProcurementFeeMode === 'UNIT_PRICE') {
                  procurementManagementFee = this.calculateProcurementManagementFee(item);
                } else {
                  // 合计模式：采管费为0
                  procurementManagementFee = 0;
                }
              }
              
              return {
                logisticsInfoId: item.logisticsInfoId, // 使用物流信息ID而不是运单号
                shipmentListId: item.id,
                orderId: item.orderId, // 直接使用API返回的订单ID
                orderItemId: item.orderItemId || null, // 订单项目ID
                settledQuantity: item.settlement_quantity,
                unitPrice: this.roundUnitPrice(unitPrice || (item.pricingMethod === 'FLOATING' ? this.calculateFloatingSettlementPrice(item) : item.taxInclusiveUnitPrice)),
                actualUnitPrice: hasActualUnitPrice ? actualUnitPrice : null, // 实际单价（仅固定价产品且有采管费时保存）
                settledAmount: settledAmount, // 如果有实际单价，使用实际单价计算的含税金额；否则按原逻辑
                taxRate: item.taxRate / 100, // 税率已经是整数，转换为小数用于计算
                taxAmount: taxAmount,
                totalAmount: hasActualUnitPrice ? settledAmount : this.calculateProductSettlementAmount(item), // 如果有实际单价，使用实际单价计算的含税金额；否则按原逻辑
                procurementManagementFee: procurementManagementFee, // 采管费
                // 根据API文档使用正确的字段名
                itemPricingMethod: item.pricingMethod || 'FIXED',
                // 固定价产品字段
                itemTaxExclusiveUnitPrice: item.pricingMethod === 'FIXED' ? (item.taxExclusiveUnitPrice || null) : null,
                itemTaxInclusiveUnitPrice: item.pricingMethod === 'FIXED' ? (item.taxInclusiveUnitPrice || null) : null,
                // 变动价产品字段
                itemTaxInclusiveBasePrice: item.pricingMethod === 'FLOATING' ? item.taxInclusiveBasePrice : null,
                itemTaxInclusiveFloatingPrice: item.pricingMethod === 'FLOATING' ? item.taxInclusiveFloatingPrice : null,
                remark: item.pricingMethod === 'FLOATING' 
                  ? `结算数量: ${item.settlement_quantity} ${item.unit}，基础价: ${this.formatUnitPrice(item.taxInclusiveBasePrice)}，变动价: ${this.formatUnitPrice(item.taxInclusiveFloatingPrice)}，结算价: ${this.formatUnitPrice(unitPrice || this.calculateFloatingSettlementPrice(item))}`
                  : `结算数量: ${item.settlement_quantity} ${item.unit}`
              };
            })
          };
          
          const response = await create_settlement(settlementData);
          
          if (response.data && response.data.success) {
            const settlementId = response.data.data.id;
// 根据是否需要供应商审核显示不同的成功消息
            if (this.supplierAuditForm.needSupplierAudit) {
              this.$message.success('结算申请提交成功！已发送给供应商审核，审核通过后需求方可见。');
            } else {
              this.$message.success('结算申请提交成功！需求方现在可以查看并确认。');
            }
            this.confirmDialogVisible = false;
            
            // 跳转到结算详情页面
            this.$router.push(`/settlement/settlement-detail/${settlementId}`);
          } else {
            this.$message.error('结算申请提交失败: ' + (response.data?.message || '未知错误'));
          }
        } catch (error) {
          console.error('提交结算申请失败:', error);
          this.$message.error('结算申请提交失败: ' + (error.message || '网络错误'));
        } finally {
          this.submitLoading = false;
        }
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    // 打印结算信息
    async printSettlement() {
      const printArea = document.getElementById('printArea');
      const printContent = printArea.innerHTML;
      
      // 创建打印窗口
      const printWindow = window.open('', '_blank', 'height=600,width=800');
      printWindow.document.write(`
        <html>
          <head>
            <title>结算确认单打印</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                padding: 20px; 
                margin: 0;
                background: white;
              }
              h3 { 
                margin: 0 0 15px 0; 
                color: #303133; 
                font-size: 18px;
                border-bottom: 2px solid #409EFF;
                padding-bottom: 8px;
              }
              .summary-card { 
                border: 1px solid #EBEEF5; 
                border-radius: 4px; 
                margin-top: 20px; 
                padding: 15px;
                background-color: #f0f9ff;
              }
              .summary-title {
                font-size: 16px;
                font-weight: bold;
                color: #303133;
                margin-bottom: 10px;
              }
              .summary-content {
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              .summary-item {
                display: flex;
                align-items: center;
                margin-right: 20px;
              }
              .summary-item span:first-child {
                margin-right: 8px;
                color: #606266;
              }
              .summary-item span:last-child {
                font-weight: bold;
                color: #e6a23c;
                font-size: 16px;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 10px; 
                font-size: 12px;
              }
              table, th, td { 
                border: 1px solid #EBEEF5; 
              }
              th { 
                background-color: #F5F7FA; 
                padding: 8px; 
                text-align: center; 
                font-weight: bold;
                color: #606266;
              }
              td { 
                padding: 6px 8px; 
                text-align: center;
              }
              .products-header {
                display: none;
              }
              .hide-on-print { 
                display: none !important; 
              }
              @media print {
                body { 
                  margin: 0; 
                  padding: 10px;
                }
                .products-header {
                  display: block;
                  margin-bottom: 15px;
                }
                h3 {
                  margin: 0 0 10px 0;
                  font-size: 16px;
                }
                table {
                  font-size: 11px;
                }
                th, td {
                  padding: 4px 6px;
                }
              }
            </style>
          </head>
          <body>
            <h3>结算确认单</h3>
            ${printContent}
          </body>
        </html>
      `);
      
      const selectedProductIds = Array.from(this.selectedProductsMap.values())
        .map(item => item.id || item.orderListId || item.orderId)
        .filter(Boolean)
        .sort()
      await installPlatformPrintFooter(printWindow, null, {
        documentType: 'SETTLEMENT_DRAFT',
        documentKey: buildPrintDocumentKey(this.confirmSettlementTitle, selectedProductIds)
      });
      printWindow.document.close();
      printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
      };
    },
    
    // 显示结算详情
    showSettlementDetails() {
      // 从 selectedProductsMap 中获取所有已选择的产品（跨分页）
      const selectedProducts = Array.from(this.selectedProductsMap.values());
      
      const details = selectedProducts.map(item => ({
        订单编号: item.orderId,
        需求方: item.demandParty,
        产品名称: item.productName,
        规格型号: item.specification,
        结算数量: `${item.settlement_quantity} ${item.unit}`,
        含税单价: `${this.formatUnitPrice(item.taxInclusiveUnitPrice)}`,
        税率: `${item.taxRate}%`,
        结算金额: `${this.formatAmount(this.calculateProductSettlementAmount(item))}`
      }));
      
      this.$alert(`
        <div style="text-align: left;">
          <h3>结算详情</h3>
          <p><strong>选择产品数量:</strong> ${this.selectedProductsCount} 项</p>
          <p><strong>结算总金额:</strong> ${this.formatAmount(this.getConfirmDialogSettlementTotal())}</p>
          <br>
          <h4>产品明细:</h4>
          ${details.map(item => 
            Object.entries(item).map(([key, value]) => `<p><strong>${key}:</strong> ${value}</p>`).join('')
          ).join('<hr>')}
        </div>
      `, '结算申请成功', {
        dangerouslyUseHTMLString: true,
        confirmButtonText: '确定'
      });
    },
    
    // 格式化金额
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00';
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 格式化单价（固定保留5位小数）
    formatUnitPrice(amount) {
      if (amount === undefined || amount === null) return '0.00000';
      const num = Number(amount);
      return isNaN(num) ? '0.00000' : num.toFixed(5);
    },

    roundUnitPrice(amount) {
      const num = Number(amount);
      return isNaN(num) ? 0 : Number(num.toFixed(5));
    },
    
    // 格式化数量（保留4位小数，去除末尾无意义的0）
    formatQuantity(quantity) {
      if (quantity === undefined || quantity === null) return '0';
      const num = parseFloat(quantity);
      if (isNaN(num)) return '0';
      // 先格式化为4位小数，然后去除末尾的0和小数点（如果有）
      const formatted = num.toFixed(4);
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 格式化采管费单价
    formatProcurementFeeUnitPrice(amount) {
      return this.formatUnitPrice(amount);
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          // 如果不是有效日期，尝试其他格式解析
          if (typeof dateString === 'string') {
            // 尝试解析常见的日期格式 YYYY-MM-DD
            const parts = dateString.split('-');
            if (parts.length === 3) {
              return dateString; // 已经是YYYY-MM-DD格式
            }
          }
          return dateString || '-';
        }
        
        // 格式化为YYYY-MM-DD
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } catch (e) {
        console.error('日期格式化错误:', e);
        return dateString || '-';
      }
    },
    
    // 返回
    goBack() {
      this.$router.push('/settlement/settlements-list');
    }
  }
};
</script>

<style lang="scss" scoped>
.create-settlement-container {
  padding: 20px;
  background-color: #fff;
  min-height: calc(100vh - 84px);
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .search-area {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    
    .search-input {
      width: 200px;
      margin-right: 15px;
      margin-bottom: 10px;
    }
    
    .el-button {
      margin-bottom: 10px;
      margin-right: 10px;
    }
  }
  
  .products-section {
    margin-bottom: 20px;
    
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      
      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
    }
    
    .settlement-status-tabs,
    .pricing-tabs {
      margin-bottom: 20px;
      
      .tab-label {
        display: flex;
        align-items: center;
        
        i {
          margin-right: 5px;
        }
      }
      
      ::v-deep .el-tabs__header {
        margin: 0 0 15px 0;
      }
      
      ::v-deep .el-tabs__nav-wrap::after {
        background-color: #e4e7ed;
      }
      
      ::v-deep .el-tabs__active-bar {
        background-color: #409EFF;
      }
      
      ::v-deep .el-tabs__item {
        font-size: 14px;
        font-weight: 500;
        
        &.is-active {
          color: #409EFF;
        }
        
        &:hover {
          color: #409EFF;
        }
      }
    }

    ::v-deep .settled-product-row {
      background-color: #f5f7fa;
      color: #909399;
    }
  }
  
  .settlement-summary {
    margin-bottom: 20px;
    
    .summary-card {
      padding: 20px;
      background-color: #f0f9ff;
      border: 1px solid #b3d8ff;
      border-radius: 4px;
      
      .summary-title {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 15px;
      }
      
      .summary-content {
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          span {
            font-size: 14px;
            color: #303133;
            
            &.amount {
              font-size: 16px;
              font-weight: 500;
              color: #2c73eb;
            }
            
            &.highlight {
              font-size: 18px;
              font-weight: bold;
              color: #e6a23c;
            }
          }
        }
      }
    }
  }
  
  .action-buttons {
    margin-top: 30px;
    text-align: center;
    
    .el-button {
      margin: 0 10px;
    }
  }
  
  .el-table {
    margin-bottom: 20px;
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }
  
// 剩余数量为0时的样式
.text-danger {
  color: #f56c6c;
  font-weight: bold;
}

// 变动价结算价样式
.settlement-price {
  color: #e6a23c;
  font-weight: bold;
}
}

// 确认弹窗样式
.confirm-content {
  .confirm-summary {
    margin-bottom: 20px;
    
    .summary-card {
      padding: 20px;
      background-color: #f0f9ff;
      border: 1px solid #b3d8ff;
      border-radius: 4px;
      
      .summary-title {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 15px;
      }
      
      .summary-content {
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          span {
            font-size: 14px;
            color: #303133;
            
            &.amount {
              font-size: 16px;
              font-weight: 500;
              color: #2c73eb;
            }
            
            &.highlight {
              font-size: 18px;
              font-weight: bold;
              color: #e6a23c;
            }
          }
        }
      }
    }
  }
  
  .confirm-settlement-title-section {
    margin-bottom: 20px;
    padding: 12px 0;
    .form-tip {
      margin-top: 6px;
      font-size: 12px;
      color: #909399;
    }
  }
  
  .confirm-products {
    .products-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      
      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
    }
    
    .el-table {
      margin-bottom: 20px;
    }
  }
  
  // 结算配置区域样式（包含结算日期和采管费比率）
  .settlement-config-section {
    margin-top: 20px;
    padding: 20px;
    background-color: #f0f9ff;
    border: 1px solid #b3d8ff;
    border-radius: 4px;
    
    .section-title {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 15px;
      padding-left: 10px;
      border-left: 4px solid #409EFF;
    }
    
    .el-form-item {
      margin-bottom: 0;
    }
  }
  
  // 供应商审核选择区域样式
  .supplier-audit-section {
    margin-top: 20px;
    padding: 20px;
    background-color: #f9f9f9;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    
    .section-title {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 15px;
      padding-left: 10px;
      border-left: 4px solid #409EFF;
    }
    
    .el-form-item {
      margin-bottom: 15px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>
